import { API_BASE_URL, ROUTES } from './config';
import { errorFromResponse } from './errors';
import { ensureFreshToken, http, refreshTokens } from './http';
import type { CampaignMediaInput, CampaignMediaItem, ProductMedia } from '../types/ecommerce';

// Multipart bodies bypass execute() (it always JSON-encodes), so these replicate its auth
// handling: proactively refresh a stale token before sending (ensureFreshToken — a token
// sitting unused in tokenStore does not refresh itself just because time passes), plus a
// reactive refresh-and-retry-once if the token was invalidated server-side in between
// (revoked, clock drift) despite looking fresh client-side.
async function send(path: string, init: RequestInit, attempt = 0): Promise<Response> {
  const token = attempt === 0 ? await ensureFreshToken() : (await refreshTokens())?.accessToken;
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: token ? { ...init.headers, Authorization: `Bearer ${token}` } : init.headers,
  });
  if (response.status === 401 && attempt === 0) return send(path, init, attempt + 1);
  return response;
}

async function uploadMultipart<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('file', file);

  const response = await send(path, { method: 'POST', body: form });
  if (!response.ok) {
    const problem = await response.json().catch(() => null);
    throw errorFromResponse(response.status, problem, response.headers.get('X-Correlation-Id'));
  }
  return response.json();
}

async function deleteWithAuth(path: string): Promise<void> {
  const response = await send(path, { method: 'DELETE' });
  if (!response.ok && response.status !== 204) {
    const problem = await response.json().catch(() => null);
    throw errorFromResponse(response.status, problem, response.headers.get('X-Correlation-Id'));
  }
}

/**
 * ProductMediaController client — native image/video upload for Products, HeroSlides and
 * Campaigns, built inside ProductService itself (NOT TenantService's generic `imagesApi`
 * above). Products already have an id by the time media is attached, so those endpoints
 * create a real child row and return it; HeroSlides/Campaigns are still single ImageUrl/
 * BannerImageUrl string fields, so their endpoints just return a URL for the form to fill in.
 */
export const productMediaApi = {
  uploadProductImage(productId: string, file: File): Promise<ProductMedia> {
    return uploadMultipart(`${ROUTES.productMedia}/products/${productId}/images`, file);
  },
  uploadProductVideo(productId: string, file: File): Promise<ProductMedia> {
    return uploadMultipart(`${ROUTES.productMedia}/products/${productId}/videos`, file);
  },
  removeProductImage(productId: string, imageId: string): Promise<void> {
    return deleteWithAuth(`${ROUTES.productMedia}/products/${productId}/images/${imageId}`);
  },
  removeProductVideo(productId: string, videoId: string): Promise<void> {
    return deleteWithAuth(`${ROUTES.productMedia}/products/${productId}/videos/${videoId}`);
  },

  async uploadHeroSlideImage(file: File): Promise<string> {
    return (await uploadMultipart<{ url: string }>(`${ROUTES.productMedia}/hero-slides/image`, file)).url;
  },
  async uploadHeroSlideVideo(file: File): Promise<string> {
    return (await uploadMultipart<{ url: string }>(`${ROUTES.productMedia}/hero-slides/video`, file)).url;
  },
  async uploadCampaignImage(file: File): Promise<string> {
    return (await uploadMultipart<{ url: string }>(`${ROUTES.productMedia}/campaigns/image`, file)).url;
  },
  async uploadCampaignVideo(file: File): Promise<string> {
    return (await uploadMultipart<{ url: string }>(`${ROUTES.productMedia}/campaigns/video`, file)).url;
  },
  async uploadPromoTileImage(file: File): Promise<string> {
    return (await uploadMultipart<{ url: string }>(`${ROUTES.productMedia}/promo-tiles/image`, file)).url;
  },
  async uploadCategoryImage(file: File): Promise<string> {
    return (await uploadMultipart<{ url: string }>(`${ROUTES.productMedia}/categories/image`, file)).url;
  },
};

/**
 * A campaign's real multi-item media gallery (CampaignMedia child rows) — distinct from the
 * uploadCampaignImage/uploadCampaignVideo pair above, which only fill the single
 * BannerImageUrl/BannerVideoUrl hero fields. Lives on ProductCampaignsController
 * (`/api/v1/product-campaigns/{id}/media`), not ProductMediaController, since it owns the
 * CampaignMedia rows — but image/video uploads still go through the same disk-backed
 * ProductMediaStorage, hence the shared `uploadMultipart`/`send` helpers here.
 */
export const campaignGalleryApi = {
  list(campaignId: string): Promise<CampaignMediaItem[]> {
    return http.get(`${ROUTES.productCampaigns}/${campaignId}/media`, { anonymous: true });
  },
  uploadImage(campaignId: string, file: File): Promise<CampaignMediaItem> {
    return uploadMultipart(`${ROUTES.productCampaigns}/${campaignId}/media/upload?isVideo=false`, file);
  },
  uploadVideo(campaignId: string, file: File): Promise<CampaignMediaItem> {
    return uploadMultipart(`${ROUTES.productCampaigns}/${campaignId}/media/upload?isVideo=true`, file);
  },
  addEmbed(campaignId: string, body: CampaignMediaInput): Promise<CampaignMediaItem> {
    return http.post(`${ROUTES.productCampaigns}/${campaignId}/media/embed`, body);
  },
  remove(campaignId: string, mediaId: string): Promise<void> {
    return deleteWithAuth(`${ROUTES.productCampaigns}/${campaignId}/media/${mediaId}`);
  },
  reorder(campaignId: string, orderedMediaIds: string[]): Promise<void> {
    return http.put(`${ROUTES.productCampaigns}/${campaignId}/media/reorder`, { orderedMediaIds });
  },
};
